import {
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent,
  type RefObject,
} from "react";
import { useFloatingSurfaceResize } from "@/hooks/use-floating-surface-resize";
import type { ModalPosition } from "@/hooks/use-draggable-modal";
import {
  calculateFocusSurfaceBox,
  calculateFocusWindowBox,
  focusWindowModeForPoint,
  type FocusWindowBox,
  type FocusWindowMode,
} from "@/lib/focus-window-layout";
import { requestNativeGlassRefresh } from "./use-native-glass-surfaces";

export interface ChatSnapPreview {
  mode: Exclude<FocusWindowMode, "normal">;
  box: FocusWindowBox;
}

const DRAG_INSET = 12;
const DRAG_EXCLUDE =
  "input, textarea, button, [role='tab'], [role='option'], a, [data-no-drag]";

function surfaceBox() {
  return calculateFocusSurfaceBox({
    width: window.innerWidth,
    height: window.innerHeight,
  });
}

function previewAt(event: {
  clientX: number;
  clientY: number;
}): ChatSnapPreview | null {
  const surface = surfaceBox();
  const mode = focusWindowModeForPoint(
    { x: event.clientX, y: event.clientY },
    surface,
  );
  return mode ? { mode, box: calculateFocusWindowBox(mode, surface) } : null;
}

/** Shared window geometry for detached chats; glass is collected after each DOM commit. */
export function useDetachedChatWindow({
  open,
  resetKey,
  surfaceRef,
  defaultSize,
  minSize,
  viewportInset = 16,
  onPositionCommit,
  onDragStart,
}: {
  open: boolean;
  resetKey: string | null;
  surfaceRef: RefObject<HTMLDivElement | null>;
  defaultSize: { width: number; height: number };
  minSize: { width: number; height: number };
  viewportInset?: number;
  onPositionCommit?: (position: ModalPosition) => void;
  onDragStart?: (
    event: PointerEvent<HTMLDivElement>,
    box: FocusWindowBox,
  ) => void;
}) {
  const resize = useFloatingSurfaceResize({
    open,
    resetKey,
    surfaceRef,
    defaultSize,
    minSize,
    viewportInset,
  });
  const [position, setPosition] = useState<ModalPosition | null>(null);
  const [windowBox, setWindowBox] = useState<FocusWindowBox | null>(null);
  const [snapPreview, setSnapPreview] = useState<ChatSnapPreview | null>(null);
  const normalBox = useRef<FocusWindowBox | null>(null);
  const snapped = useRef(false);
  const resizing = useRef(false);
  const drag = useRef<{
    pointerId: number;
    offsetX: number;
    offsetY: number;
    width: number;
    height: number;
    startX: number;
    startY: number;
    moved: boolean;
  } | null>(null);

  useLayoutEffect(() => {
    drag.current = null;
    normalBox.current = null;
    snapped.current = false;
    setPosition(null);
    setWindowBox(null);
    setSnapPreview(null);
  }, [open, resetKey]);

  const style = windowBox
    ? {
        ...resize.style,
        width: windowBox.width,
        height: windowBox.height,
        minWidth: 1,
        minHeight: 1,
      }
    : resize.style;
  useLayoutEffect(() => {
    if (!open) return;
    requestNativeGlassRefresh({ trackTransition: false });
  }, [open, position?.x, position?.y, style.width, style.height]);

  const move = (event: PointerEvent<HTMLDivElement>) => {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    if (
      !current.moved &&
      Math.hypot(
        event.clientX - current.startX,
        event.clientY - current.startY,
      ) < 3
    )
      return;
    current.moved = true;
    if (snapped.current && normalBox.current) {
      current.offsetX =
        Math.min(current.offsetX / current.width, 1) * normalBox.current.width;
      current.width = normalBox.current.width;
      current.height = normalBox.current.height;
      setWindowBox(normalBox.current);
      snapped.current = false;
    }
    setPosition({
      x: Math.min(
        Math.max(DRAG_INSET, event.clientX - current.offsetX),
        Math.max(DRAG_INSET, window.innerWidth - current.width - DRAG_INSET),
      ),
      y: Math.min(
        Math.max(DRAG_INSET, event.clientY - current.offsetY),
        Math.max(DRAG_INSET, window.innerHeight - current.height - DRAG_INSET),
      ),
    });
    const preview = previewAt(event);
    setSnapPreview((previous) =>
      previous?.mode === preview?.mode ? previous : preview,
    );
  };

  const end = (event: PointerEvent<HTMLDivElement>, cancelled = false) => {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    drag.current = null;
    setSnapPreview(null);
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
    if (!current.moved) return;
    const rect = surfaceRef.current?.getBoundingClientRect();
    if (!rect) return;
    const preview = cancelled ? null : previewAt(event);
    if (preview) {
      if (!snapped.current)
        normalBox.current = {
          x: rect.left,
          y: rect.top,
          width: current.width,
          height: current.height,
        };
      snapped.current = true;
      setWindowBox(preview.box);
      setPosition({ x: preview.box.x, y: preview.box.y });
      onPositionCommit?.(preview.box);
    } else {
      onPositionCommit?.({ x: rect.left, y: rect.top });
    }
  };

  return {
    position,
    size: windowBox ?? resize.size,
    style,
    snapPreview,
    dragHandleProps: {
      onPointerDown: (event: PointerEvent<HTMLDivElement>) => {
        if (event.button !== 0) return;
        const target = event.target as HTMLElement;
        if (
          target.closest(DRAG_EXCLUDE) ||
          !target.closest("[data-detached-frame-drag-handle]")
        )
          return;
        const rect = surfaceRef.current?.getBoundingClientRect();
        if (!rect) return;
        drag.current = {
          pointerId: event.pointerId,
          offsetX: event.clientX - rect.left,
          offsetY: event.clientY - rect.top,
          width: rect.width,
          height: rect.height,
          startX: event.clientX,
          startY: event.clientY,
          moved: false,
        };
        event.currentTarget.setPointerCapture(event.pointerId);
        event.preventDefault();
        const restored = snapped.current ? normalBox.current : null;
        onDragStart?.(
          event,
          restored
            ? {
                x:
                  event.clientX -
                  Math.min(event.clientX - rect.left, restored.width - 24),
                y: event.clientY - Math.min(event.clientY - rect.top, 32),
                width: restored.width,
                height: restored.height,
              }
            : {
                x: rect.left,
                y: rect.top,
                width: rect.width,
                height: rect.height,
              },
        );
      },
      onPointerMove: move,
      onPointerUp: (event: PointerEvent<HTMLDivElement>) => end(event),
      onPointerCancel: (event: PointerEvent<HTMLDivElement>) =>
        end(event, true),
      onLostPointerCapture: () => {
        drag.current = null;
        setSnapPreview(null);
      },
    },
    resizeHandleProps: {
      ...resize.resizeHandleProps,
      onPointerDown: (event: PointerEvent<HTMLButtonElement>) => {
        if (event.button !== 0) return;
        resize.resizeHandleProps.onPointerDown(event);
        resizing.current = true;
      },
      onPointerMove: (event: PointerEvent<HTMLButtonElement>) => {
        if (!resizing.current) return;
        resize.resizeHandleProps.onPointerMove(event);
        setWindowBox(null);
        snapped.current = false;
      },
      onPointerUp: (event: PointerEvent<HTMLButtonElement>) => {
        resizing.current = false;
        resize.resizeHandleProps.onPointerUp(event);
      },
      onPointerCancel: (event: PointerEvent<HTMLButtonElement>) => {
        resizing.current = false;
        resize.resizeHandleProps.onPointerCancel(event);
      },
      onLostPointerCapture: () => {
        resizing.current = false;
        resize.resizeHandleProps.onLostPointerCapture();
      },
    },
  };
}
